// frontend/src/pages/recruiter/ApplicantReview.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "./../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./../../components/ui/card";
import { Badge } from "./../../components/ui/badge";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import { Label } from "./../../components/ui/label";
import { Textarea } from "./../../components/ui/textarea";
import { Loader2, Download, CheckCircle, XCircle, MessageSquare, User, Briefcase, ExternalLink, ArrowLeft } from "lucide-react";
import api from "./../../api/client";
import { useAuth } from "../../context/AuthContext";

const statusOptions = [
  { value: "PENDING", label: "Pending", variant: "secondary" },
  { value: "ACCEPTED", label: "Accepted", variant: "default" },
  { value: "REJECTED", label: "Rejected", variant: "destructive" },
];

export default function ApplicantReview() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const apiPrefix = user?.role === "ADMIN" ? "/admin" : "/recruiter";

  useEffect(() => {
    if (user) fetchApplication();
  }, [appId, user]);

  const fetchApplication = async () => {
    try {
      const res = await api.get(`${apiPrefix}/applications/${appId}`);
      setApplication(res.data);
      setStatus(res.data.status);
    } catch (err) {
      setError("Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (user?.role === "ADMIN") {
         await api.patch(`${apiPrefix}/applications/${appId}/status`, { status, note });
      } else {
         await api.patch(`${apiPrefix}/applications/${appId}`, { status, note });
      }
      setSuccess("Status updated!");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      setError("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const getResumeUrl = (url) => {
    if (!url) return "#";
    if (url.startsWith("http")) return url;
    const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/$/, "");
    const normalizedPath = url.startsWith("/") ? url : `/${url}`;
    return `${baseUrl}${normalizedPath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="relative z-10 flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <p className="text-muted-foreground animate-pulse">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-background py-20 px-4">
        <Card className="max-w-md mx-auto relative z-10 backdrop-blur-md bg-card/60">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-lg font-medium">Application not found</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 relative overflow-hidden flex flex-col">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full flex-grow">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 backdrop-blur-sm px-3 py-1">
                {user?.role} VIEW
              </Badge>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-foreground">
              Review Application
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Evaluating <span className="text-primary font-semibold">{application.applier.applierProfile?.fullName}</span> for the <span className="text-foreground font-medium">{application.job.title}</span> position.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)} className="w-fit backdrop-blur-sm bg-background/40 border-primary/20 hover:bg-primary/10 transition-all">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to List
          </Button>
        </div>

        {success && (
          <Alert className="mb-6 border-green-500/30 bg-green-500/10 backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-300">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription className="text-green-600 dark:text-green-400 font-medium">{success}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6 backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-300">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Applicant Profile Card */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="overflow-hidden border-primary/10 bg-card/40 backdrop-blur-xl shadow-2xl">
              <div className="h-2 bg-gradient-to-r from-primary to-accent" />
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <User className="w-5 h-5 text-primary" />
                  Candidate Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center p-4 rounded-2xl bg-primary/5 border border-primary/10 mb-2">
                   <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary text-3xl font-bold mb-3 border-2 border-primary/30 shadow-inner">
                      {application.applier.applierProfile?.fullName?.charAt(0) || "C"}
                   </div>
                   <h2 className="font-bold text-xl text-center">{application.applier.applierProfile?.fullName || "Name unknown"}</h2>
                   <p className="text-sm text-muted-foreground text-center break-all">{application.applier.email}</p>
                </div>

                <div className="space-y-4">
                   <div className="grid grid-cols-1 gap-4 text-sm">
                      <div className="p-3 rounded-xl bg-background/50 border border-border/50">
                        <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mb-1">Phone</p>
                        <p className="font-semibold">{application.applier.applierProfile?.phone || "N/A"}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-background/50 border border-border/50">
                        <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mb-1">University</p>
                        <p className="font-semibold">{application.applier.applierProfile?.university || "N/A"}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-background/50 border border-border/50">
                        <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider mb-1">GPA Score</p>
                        <p className="font-semibold text-primary">{application.applier.applierProfile?.gpa || "N/A"}</p>
                      </div>
                   </div>

                   {application.applier.applierProfile?.resumeUrl && (
                      <Button
                        size="lg"
                        className="w-full bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        onClick={() => window.open(getResumeUrl(application.applier.applierProfile.resumeUrl), "_blank")}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Full Resume
                      </Button>
                   )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Application Content & Action */}
          <div className="lg:col-span-8 space-y-6">
            <Card className="border-primary/10 bg-card/40 backdrop-blur-xl shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                 <Briefcase className="w-32 h-32" />
              </div>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <Briefcase className="w-6 h-6 text-primary" />
                    {application.job.title}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Badge variant="secondary" className="bg-secondary/50 backdrop-blur-sm border-secondary-foreground/10">{application.job.location}</Badge>
                    <Badge variant="outline" className="border-primary/20 bg-primary/5 truncate">
                      Applied {new Date(application.appliedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {application.coverLetter ? (
                  <div className="space-y-3">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                       <MessageSquare className="w-5 h-5 text-primary" />
                       Cover Letter
                    </h3>
                    <div className="bg-background/60 p-6 rounded-2xl text-base leading-relaxed whitespace-pre-wrap border border-primary/5 shadow-inner">
                      {application.coverLetter}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center bg-background/40 rounded-2xl border border-dashed border-border">
                    <p className="text-muted-foreground">No cover letter provided for this application.</p>
                  </div>
                )}

                {application.resumeUrl && (
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/15 flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary dark:text-accent">
                          <Download className="w-5 h-5" />
                       </div>
                       <div>
                          <p className="font-bold text-sm">Additional Attachment</p>
                          <p className="text-xs text-muted-foreground">Attached specific resume for this role</p>
                       </div>
                    </div>
                    <Button
                      variant="ghost" 
                      size="sm"
                      className="hover:bg-primary/20"
                      onClick={() => window.open(getResumeUrl(application.resumeUrl), "_blank")}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-primary/10 bg-card/40 backdrop-blur-xl shadow-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Decision & Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Change Candidate Status</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {statusOptions.map((opt) => {
                      const isActive = status === opt.value;
                      return (
                        <Button
                          key={opt.value}
                          variant={isActive ? "default" : "outline"}
                          onClick={() => setStatus(opt.value)}
                          className={`h-16 text-base shadow-sm ${
                            isActive 
                              ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30" 
                              : "bg-background/50 border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                          }`}
                        >
                          {isActive && <CheckCircle className="w-4 h-4 mr-1" />}
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="note" className="text-base font-semibold">Decision Logic & Notes</Label>
                  <Textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Provide justification for the status change or internal comments for visibility..."
                    className="min-h-[120px] bg-background/60 border-primary/10 rounded-2xl focus:border-primary/50 transition-all resize-none p-4 shadow-inner"
                  />
                </div>

                <Button
                  onClick={updateStatus}
                  disabled={saving || status === application.status}
                  size="lg"
                  variant="premium"
                  className="w-full text-lg h-16 rounded-2xl"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                      Saving Decision...
                    </>
                  ) : (
                    "Confirm Status Update"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12 mb-8 flex flex-col items-center gap-4">
           <p className="text-sm text-muted-foreground">Review completed? Go back to continue processing other candidates.</p>
           <Button variant="link" onClick={() => navigate(-1)} className="text-primary font-bold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Application Management
           </Button>
        </div>
      </div>
    </div>
  );
}